import React from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import Fade from '@material-ui/core/Fade';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Divider from '@material-ui/core/Divider';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import lng from '../../misc/lng';
import { filterVoidOrders } from '../../utils/orderFilterFunc';
import moment from 'moment';
import DiscountDisplayGrid from '../DineIn/shoppingCart/DiscountDisplayGrid';
import { useFontSize } from '../../contexts/FontSizeContext';

const { chiLng } = lng;

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Fade ref={ref} {...props} />;
});

const useStyles = makeStyles((theme) => ({
    root: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#f5f5f5',
        overflow: 'hidden',
        width: '100%',
    },
    header: {
        backgroundColor: '#FFFFFF',
        padding: theme.spacing(2),
        display: 'flex',
        alignItems: 'center',
        borderBottom: '1px solid #E0E0E0',
        position: 'sticky',
        top: 0,
        zIndex: 10,
    },
    backButton: {
        marginRight: theme.spacing(2),
        backgroundColor: '#f5f5f5',
        '&:hover': {
            backgroundColor: '#e0e0e0',
        },
    },
    title: {
        fontSize: '18px',
        fontWeight: 'bold',
        color: '#212121',
    },
    content: {
        flex: 1,
        overflow: 'auto',
        overflowX: 'hidden',
        paddingBottom: theme.spacing(10),
        width: '100%',
    },
    orderCard: {
        // marginBottom: theme.spacing(2),
        borderRadius: 12,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        margin: '10px',
    },
    orderHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(1),
    },
    orderTime: {
        display: 'flex',
        alignItems: 'center',
        color: '#757575',
        fontSize: '14px',
    },
    orderStatus: {
        display: 'flex',
        alignItems: 'center',
        color: '#4CAF50',
        fontSize: '14px',
        fontWeight: '500',
    },
    orderItem: {
        backgroundColor: 'white',
        marginBottom: '16px',
        padding: theme.spacing(2),
        boxSizing: 'border-box',
    },
    itemInfo: {
        marginBottom: theme.spacing(1),
    },
    itemName: {
        fontSize: '18px',
        fontWeight: '500',
        color: '#212121',
    },
    priceText: {
        fontSize: '18px',
        fontWeight: 'bold',
        color: '#212121',
    },
    itemSpecs: {
        fontSize: '14px',
        color: '#757575',
        marginBottom: theme.spacing(0.5),
    },
    itemOptions: {
        fontSize: '12px',
        color: '#9E9E9E',
    },
    itemPrice: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
    },
    itemQty: {
        fontSize: '14px',
        color: '#757575',
        marginBottom: theme.spacing(0.5),
    },
    itemTotal: {
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#212121',
    },
    emptyState: {
        textAlign: 'center',
        padding: theme.spacing(4),
        color: '#757575',
    },
    emptyIcon: {
        fontSize: '48px',
        marginBottom: theme.spacing(2),
        opacity: 0.5,
    },
    divider: {
        margin: theme.spacing(1, 0),
    },
    discountSection: {
        backgroundColor: 'white',
        borderTop: '1.5px solid #efd8d8',
        borderBottom: '1.5px solid #efd8d8',
        marginBottom: '10px',
        padding: theme.spacing(2),
        boxSizing: 'border-box',
    },
    totalSection: {
        backgroundColor: 'white',
        borderTop: '1.5px solid #efd8d8',
        borderBottom: '1.5px solid #efd8d8',
        marginBottom: '10px',
        padding: theme.spacing(2),
        boxSizing: 'border-box',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    totalLabel: {
        fontSize: '18px',
        fontWeight: 'bold',
        color: '#212121',
    },
    totalAmount: {
        fontSize: '20px',
        fontWeight: 'bold',
        color: '#4CAF50',
    },
    summaryCard: {
        backgroundColor: 'white',
        borderRadius: '12px',
        margin: '10px',
        padding: theme.spacing(2),
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        border: '1px solid #e0e0e0',
    },
    summaryDivider: {
        margin: theme.spacing(1.5, 0),
        borderColor: '#e0e0e0',
    },
    summaryTotalRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
}));

export default ({ open, onClose }) => {
    const classes = useStyles();
    const { getFontSize } = useFontSize();
    const {
        tableInCharge: { orders, productSummary = {} },
    } = useSelector((store) => store.dineInReducer);

    // Dynamic font sizes
    const mealTitleFontSize = getFontSize('mealTitle');
    const priceFontSize = getFontSize('priceMain');

    const validOrders = filterVoidOrders(orders);



    // Calculate total from all orders
    const calculateOrderTotal = () => {
        return validOrders.reduce((total, order) => {
            return total + order.orderDetails.reduce((orderTotal, detail) => {
                if (detail.hasMenuSpec === 'true' && detail.productSpec) {
                    return orderTotal + detail.productSpec.reduce((specTotal, spec) => {
                        return specTotal + (spec.priceForOne * spec.qty);
                    }, 0);
                } else {
                    return orderTotal + (detail.price * detail.qty);
                }
            }, 0);
        }, 0);
    };

    const orderTotal = calculateOrderTotal();
    const { tagSetDiscountList = [], priceAfterDiscount = orderTotal } = productSummary;

    const formatOrderTime = (orderTime) => {
        return moment(orderTime).format('HH:mm');
    };

    const renderOrderItem = (orderDetail, itemIndex) => {
        // Handle potential field name variations
        const mealName = orderDetail.mealName || orderDetail.name || 'Unknown Item';
        const { simpleOptions = [], customRequest } = orderDetail;



        // Handle meals with specifications (hasMenuSpec === 'true')
        if (orderDetail.hasMenuSpec === 'true' && orderDetail.productSpec) {
            return orderDetail.productSpec.map((productSpecItem, specIndex) => (
                <div key={`${itemIndex}-${specIndex}`} className={classes.orderItem}>
                    <div className={classes.itemInfo}>
                        {/* Main line: quantity X meal name and price */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <Typography
                                variant="inherit"
                                className={classes.itemName}
                                style={{ fontSize: mealTitleFontSize }}
                            >
                                {productSpecItem.qty} X {mealName}
                            </Typography>
                            <Typography
                                variant="inherit"
                                className={classes.priceText}
                                style={{ fontSize: priceFontSize }}
                            >
                                ${(productSpecItem.priceForOne * productSpecItem.qty).toFixed(2)}
                            </Typography>
                        </div>

                        {/* Display specifications */}
                        {productSpecItem.spec && productSpecItem.spec.map((specItem, idx) => {
                            const specNameList = specItem.specOptions.map((opt) => {
                                if (opt.specPrice > 0) {
                                    return `${opt.specName} $${opt.specPrice}`;
                                }
                                return opt.specName;
                            });
                            return (
                                <Typography key={idx} variant="inherit" className={classes.itemSpecs} style={{ fontSize: getFontSize('subtitle') }}>
                                    - {specNameList.join(', ')}
                                </Typography>
                            );
                        })}

                        {/* Display simple options */}
                        {simpleOptions.length > 0 && (
                            <Typography variant="inherit" className={classes.itemOptions} style={{ fontSize: getFontSize('subtitle') }}>
                                - {simpleOptions.join(', ')}
                            </Typography>
                        )}

                        {/* Display custom request */}
                        {customRequest && (
                            <Typography variant="inherit" className={classes.itemOptions} style={{ fontSize: getFontSize('subtitle') }}>
                                - {customRequest}
                            </Typography>
                        )}
                    </div>
                </div>
            ));
        } else {
            // Handle normal meals (hasMenuSpec !== 'true')
            const { qty, price } = orderDetail;
            return (
                <div key={itemIndex} className={classes.orderItem}>
                    <div className={classes.itemInfo}>
                        {/* Main line: quantity X meal name and price */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <Typography
                                variant="inherit"
                                className={classes.itemName}
                                style={{ fontSize: mealTitleFontSize }}
                            >
                                {qty} X {mealName}
                            </Typography>
                            <Typography
                                variant="inherit"
                                className={classes.priceText}
                                style={{ fontSize: priceFontSize }}
                            >
                                ${(price * qty).toFixed(2)}
                            </Typography>
                        </div>

                        {/* Display simple options */}
                        {simpleOptions.length > 0 && (
                            <Typography variant="inherit" className={classes.itemOptions} style={{ fontSize: getFontSize('subtitle') }}>
                                - {simpleOptions.join(', ')}
                            </Typography>
                        )}

                        {/* Display custom request */}
                        {customRequest && (
                            <Typography variant="inherit" className={classes.itemOptions} style={{ fontSize: getFontSize('subtitle') }}>
                                - {customRequest}
                            </Typography>
                        )}
                    </div>
                </div>
            );
        }
    };

    const renderOrder = (order, orderIndex) => {
        return (
            <Card key={orderIndex} className={classes.orderCard}>
                <CardContent>
                    <div className={classes.orderHeader}>
                        <div className={classes.orderTime}>
                            <AccessTimeIcon style={{ fontSize: 16, marginRight: 4 }} />
                            {chiLng.orderTime}: {formatOrderTime(order.orderTime)}
                        </div>
                        <div className={classes.orderStatus}>
                            <CheckCircleIcon style={{ fontSize: 16, marginRight: 4 }} />
                            已確認
                        </div>
                    </div>

                    {order.orderDetails.map((orderDetail, itemIndex) => {
                        const renderedItems = renderOrderItem(orderDetail, itemIndex);
                        // Handle both single items and arrays of items (for spec meals)
                        return Array.isArray(renderedItems) ? renderedItems : [renderedItems];
                    }).flat()}
                </CardContent>
            </Card>
        );
    };

    const renderEmptyState = () => (
        <div className={classes.emptyState}>
            <CheckCircleIcon className={classes.emptyIcon} />
            <Typography variant="inherit" gutterBottom style={{ fontSize: mealTitleFontSize }}>
                暫無已確認訂單
            </Typography>
            <Typography variant="inherit" style={{ fontSize: getFontSize('subtitle') }}>
                您的已確認訂單將會顯示在這裡
            </Typography>
        </div>
    );

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            onClose={onClose}
            fullScreen
        >
            <div className={classes.root}>
                {/* Header */}
                <div className={classes.header}>
                    <IconButton
                        className={classes.backButton}
                        onClick={onClose}
                        aria-label="back"
                    >
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="inherit" className={classes.title} style={{ fontSize: getFontSize('sectionHeader') }}>
                        {chiLng.confirmedOrderTitle}
                    </Typography>
                </div>

                {/* Content */}
                <div className={classes.content}>
                    {validOrders.length > 0 ? (
                        <>
                            {validOrders.map((order, orderIndex) => renderOrder(order, orderIndex))}

                            {/* Merged Summary Card */}
                            <div className={classes.summaryCard}>
                                {/* Discount Section */}
                                {tagSetDiscountList.length > 0 && (
                                    <>
                                        <DiscountDisplayGrid productSummary={productSummary} />
                                        <Divider className={classes.summaryDivider} />
                                    </>
                                )}

                                {/* Total Section */}
                                <div className={classes.summaryTotalRow}>
                                    <Typography variant="inherit" className={classes.totalLabel} style={{ fontSize: priceFontSize }}>
                                        {chiLng.shoppingCartPriceTotal}
                                    </Typography>
                                    <Typography variant="inherit" className={classes.totalAmount} style={{ fontSize: priceFontSize }}>
                                        ${priceAfterDiscount.toFixed(2)}
                                    </Typography>
                                </div>
                            </div>
                        </>
                    ) : (
                        renderEmptyState()
                    )}
                </div>
            </div>
        </Dialog>
    );
};
